import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { MessageType, MESSAGE_STATUS_INDICATOR } from 'models/Message';
import { Button, Collapsible } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import { t } from 'i18next';
import {
  checkIsTextValidJSON,
  getHrefFromUrl,
  getIsCurrentUser,
  isUrl as isUrlFunc,
} from 'utils';
import api from 'api';
import ResolveImage from 'components/ResolveImage';
import { NEW_WINDOW_PROPS } from 'data/global';
import DisplayRichTextString from 'components/RichTextEditorField/components/DisplayRichTextString';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import SoftSignalIcon from 'components/SoftSignalIcon';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import { countTextLengthFromJson } from 'components/RichTextEditorField/utils';
import SentTicksIcon from 'icons/SentTicks.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useActionOnAtCopy from 'hooks/useActionOnAtCopy';
import useDeleteMessage from 'hooks/useDeleteMessage';
import TrashIcon from 'icons/Trash.icon';
import ReactionPopup from 'components/ReactionPopup';
import { ReactionType } from 'models/Reactions';
import ReactionList from 'components/ReactionList';
import { getReactionNumbers } from '../../helpers';

import './ChatMessage.styles.scss';

type ChatMessageProps = {
  className?: string;
  messageId: string;
  buyerId: string;
  isBuyer: boolean;
  currentUserImg: string;
  companyLogoImg: string;
  interlocutorImg: string;
  prevMsgUserId: string;
  createdBy: string;
  dataRoomId: string;
  dataRoomName: string;
  disabled: boolean;
  onDeleteMessageRequest: (messageId: string) => void;
  updateMessage: (message: Partial<MessageType>) => void;
} & Omit<MessageType, '_id'> &
  ExtractPropTypes<typeof Collapsible>;

const ChatMessage: React.FC<ChatMessageProps> = (props) => {
  const {
    messageId,
    className,
    text,
    userId,
    createdAt,
    buyerId,
    isBuyer,
    currentUserImg,
    companyLogoImg,
    interlocutorImg,
    prevMsgUserId,
    statusIndicator: indicator,
    showDate = true,
    files,
    createdBy,
    sessionId,
    isInterestContext,
    softSignal,
    dataRoomNode,
    dataRoomNodeName,
    seenAt,
    dataRoomName,
    isPulseCheck,
    isDeleted,
    reaction,
    disabled,
    dataRoomId,
    onDeleteMessageRequest,
    updateMessage,
    ...rest
  } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { openNode } = useContext(DocumentViewContext);

  const isPrevBuyerMsg = useMemo(
    () => buyerId === prevMsgUserId,
    [buyerId, prevMsgUserId],
  );
  const isBuyerMsg = useMemo(() => buyerId === userId, [buyerId, userId]);
  const rightSideAvatar = useMemo(
    () => (isBuyer ? currentUserImg : companyLogoImg),
    [companyLogoImg, currentUserImg, isBuyer],
  );
  const leftSideAvatar = useMemo(
    () => (isBuyer ? companyLogoImg : interlocutorImg),
    [companyLogoImg, interlocutorImg, isBuyer],
  );
  const isMyMessage = useMemo(
    () => (isBuyer && isBuyerMsg) || (!isBuyer && !isBuyerMsg),
    [isBuyer, isBuyerMsg],
  );
  const isPrevMyMessage = useMemo(
    () => (isBuyer && isPrevBuyerMsg) || (!isBuyer && !isPrevBuyerMsg),
    [isBuyer, isPrevBuyerMsg],
  );
  const errorIndicator = useMemo(
    () => indicator === MESSAGE_STATUS_INDICATOR.ERROR,
    [indicator],
  );
  const notSentIndicator = useMemo(
    () => indicator === MESSAGE_STATUS_INDICATOR.NOT_SENT,
    [indicator],
  );

  const showSeen = useMemo(
    () => !isDeleted && (isSuperAdmin || isMyMessage) && !notSentIndicator,
    [isDeleted, isMyMessage, isSuperAdmin, notSentIndicator],
  );

  const { thumbsDown, thumbsUp, questionMark } = useMemo(
    () => getReactionNumbers(reaction),
    [reaction],
  );

  const classes = classNames(
    'chat-message',
    {
      'chat-message--sent': isMyMessage,
      'chat-message--received': !isMyMessage,
      'chat-message--error': errorIndicator,
      'chat-message--not-sent': notSentIndicator,
      'chat-message--seen': showSeen,
      'chat-message--deleted': isDeleted,
    },
    className,
  );

  const canDeleteMessage = useDeleteMessage({ createdAt, seenAt });

  const areActionsDisabled = useMemo(
    () => disabled || isDeleted || notSentIndicator,
    [disabled, isDeleted, notSentIndicator],
  );

  const showCanDeleteMessage = useMemo(
    () => !areActionsDisabled && canDeleteMessage && getIsCurrentUser(userId),
    [areActionsDisabled, canDeleteMessage, userId],
  );

  const showCanReactToMessage = useMemo(
    () => !areActionsDisabled && !isMyMessage,
    [areActionsDisabled, isMyMessage],
  );

  const isJson = useMemo(() => checkIsTextValidJSON(text), [text]);

  const isEmpty = useMemo(
    () => (isJson ? !countTextLengthFromJson(text) : !text),
    [isJson, text],
  );

  const getActionOnAtCopy = useActionOnAtCopy();

  const renderText = useMemo(() => {
    if (isEmpty) return null;

    if (isJson) return <DisplayRichTextString>{text}</DisplayRichTextString>;

    const words = text?.split(' ').map((el: string) => ({
      el,
      isUrl: isUrlFunc(el),
    }));

    return words.map(({ el, isUrl }) =>
      isUrl ? (
        <Fragment key={el}>
          <a
            href={getHrefFromUrl(el)}
            onClick={(e) => e.stopPropagation()}
            {...NEW_WINDOW_PROPS}
          >
            {el}
          </a>
          {` `}
        </Fragment>
      ) : (
        `${el} `
      ),
    );
  }, [isEmpty, isJson, text]);

  const hasFiles = useMemo(() => !!files?.length, [files?.length]);

  const renderedFiles = useMemo(
    () =>
      files?.map(({ chatSessionFileId, name, originalType, type, url }) => (
        <MessageFile
          key={chatSessionFileId}
          id={chatSessionFileId}
          name={name}
          type={originalType || type}
          url={url}
          dataRoomId={dataRoomId}
          dataRoomName={dataRoomName}
          onFilePreview={(id: string) =>
            api.messages.previewMessageFile(sessionId, id)
          }
        />
      )),
    [files, sessionId, dataRoomId, dataRoomName],
  );

  const onReact = useCallback(
    async (nextReaction: ReactionType) => {
      api.messages.addMessageReaction(messageId, nextReaction);
      updateMessage({
        _id: messageId,
        reaction: nextReaction === reaction ? undefined : nextReaction,
      });
    },
    [messageId, reaction, updateMessage],
  );

  if (!hasFiles && isEmpty && !isDeleted) return null;

  return (
    <div className="chat-message__container" data-message-id={`${messageId}`}>
      <Collapsible
        {...rest}
        className={classes}
        trigger={
          <>
            <div className="chat-message__content-wrapper">
              {!isMyMessage ? (
                !isPrevMyMessage && prevMsgUserId ? (
                  <div className="empty-avatar" />
                ) : (
                  <ResolveImage asAvatar src={leftSideAvatar} />
                )
              ) : null}

              <div className="chat-message__content">
                {isInterestContext && (
                  <p className="chat-message__content__interest-context">
                    {t('interestContext')}
                  </p>
                )}

                {softSignal && (
                  <div className="chat-message__content__soft-signal">
                    {isPulseCheck && <p>{t('pulseCheck')}:</p>}

                    {(dataRoomNode || dataRoomNodeName) && (
                      <p>{t('softSignal')}:</p>
                    )}
                    {dataRoomNode ? (
                      <Button
                        variant="link"
                        onClick={() => openNode(dataRoomNode, dataRoomName)}
                      >
                        {dataRoomNode?.name}
                      </Button>
                    ) : (
                      dataRoomNodeName && <p>{dataRoomNodeName}</p>
                    )}

                    <SoftSignalIcon softSignal={softSignal} />
                  </div>
                )}

                <div className="chat-message__content__text">
                  {isDeleted ? (
                    t('thisMessageWasDeleted')
                  ) : (
                    <>
                      {showCanDeleteMessage && (
                        <Button
                          icon={TrashIcon}
                          variant="icon"
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteMessageRequest(messageId);
                          }}
                        />
                      )}

                      {showCanReactToMessage && (
                        <ReactionPopup
                          activeReaction={reaction}
                          onReact={onReact}
                        />
                      )}

                      {renderText}

                      {hasFiles && (
                        <div className="chat-message__content__text__files">
                          {renderedFiles}
                        </div>
                      )}

                      {showSeen && (
                        <SentTicksIcon
                          className={classNames({ seen: !!seenAt })}
                        />
                      )}
                    </>
                  )}

                  <ReactionList
                    thumbsUp={thumbsUp}
                    thumbsDown={thumbsDown}
                    questionMark={questionMark}
                    showPopup={false}
                  />
                </div>
              </div>

              {isMyMessage ? (
                isPrevMyMessage && prevMsgUserId ? (
                  <div className="empty-avatar" />
                ) : (
                  <ResolveImage asAvatar src={rightSideAvatar} />
                )
              ) : null}
            </div>

            {errorIndicator && (
              <span className="chat-message__error-indicator">
                {t('errorIndicator')}
              </span>
            )}
          </>
        }
      >
        <span
          className="chat-message__time"
          onClick={(e) => e.stopPropagation()}
        >
          {getActionOnAtCopy('sentOn', createdAt)}
        </span>
        {showSeen && seenAt && (
          <span
            className="chat-message__time"
            onClick={(e) => e.stopPropagation()}
          >
            {getActionOnAtCopy('seenOn', seenAt)}
          </span>
        )}
      </Collapsible>
      {!!showDate && (
        <div className="chat-message__date">
          <span className="chat-message__date__line" />
          <span className="chat-message__date__content">{showDate}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatMessage);
